import { authGuard } from "./core/auth/auth.guard";
import { surveyAdminGuard } from "./core/auth/survey-admin.guard";

import type { Routes } from "@angular/router";

/**
 * Every navigable route carries a `title` so the browser tab reflects the tool
 * being used (issue #293). Angular's default TitleStrategy leaves the previous
 * title in place for routes without one, so a missing title would leak the
 * previous page's name into the tab — `app.routes.spec.ts` guards against that.
 * Titles use the names the app itself shows for each tool.
 */
export const routes: Routes = [
  {
    path: "login",
    title: "Sign In",
    loadComponent: () => import("./features/login/login.component").then((m) => m.LoginComponent),
  },
  {
    path: "survey-manager",
    title: "Survey Manager",
    canActivate: [authGuard],
    loadComponent: () => import("./features/survey-manager/survey-manager.component").then((m) => m.SurveyManagerComponent),
  },
  {
    path: "create",
    title: "Create Survey",
    canActivate: [authGuard],
    loadComponent: () => import("./features/create-survey/create-survey.component").then((m) => m.CreateSurveyComponent),
  },
  {
    path: "form-builder",
    title: "Form Builder",
    canActivate: [authGuard],
    loadComponent: () => import("./features/form-builder/form-builder.component").then((m) => m.FormBuilderComponent),
  },
  {
    path: "form-builder/:id",
    title: "Form Builder",
    canActivate: [authGuard],
    loadComponent: () => import("./features/form-builder/form-builder.component").then((m) => m.FormBuilderComponent),
  },
  {
    path: "survey-assignment",
    title: "Survey Assignment",
    canActivate: [authGuard],
    loadComponent: () => import("./features/survey-assignment/survey-assignment.component").then((m) => m.SurveyAssignmentComponent),
  },
  {
    path: "survey-summary",
    title: "Survey Summaries",
    canActivate: [authGuard],
    loadComponent: () => import("./features/survey-summary/survey-summary.component").then((m) => m.SurveySummaryComponent),
  },
  {
    path: "survey-summary/:id",
    title: "Survey Summary",
    canActivate: [authGuard],
    loadComponent: () => import("./features/survey-summary/survey-instance-detail.component").then((m) => m.SurveyInstanceDetailComponent),
  },
  {
    path: "survey-summary/:id/edit",
    title: "Edit Survey Summary",
    canActivate: [authGuard],
    loadComponent: () => import("./features/survey-summary/survey-instance-edit.component").then((m) => m.SurveyInstanceEditComponent),
  },
  {
    path: "survey-summary/:id/media",
    title: "Media & Documents",
    canActivate: [authGuard],
    loadComponent: () => import("./features/survey-summary/survey-instance-media.component").then((m) => m.SurveyInstanceMediaComponent),
  },
  {
    path: "survey-approval",
    title: "Survey Approvals",
    canActivate: [authGuard],
    loadComponent: () => import("./features/survey-approval/survey-approval.component").then((m) => m.SurveyApprovalComponent),
  },
  {
    path: "scheduling/at-risk",
    title: "Scheduling · At Risk",
    canActivate: [authGuard],
    loadComponent: () => import("./features/scheduling-dashboard/scheduling-dashboard.component").then((m) => m.SchedulingDashboardComponent),
  },
  {
    path: "scheduling/reports",
    title: "Scheduling · On-time Rate",
    canActivate: [authGuard],
    loadComponent: () => import("./features/scheduling-reports/scheduling-reports.component").then((m) => m.SchedulingReportsComponent),
  },
  {
    path: "notifications",
    title: "Notifications",
    canActivate: [authGuard, surveyAdminGuard],
    loadComponent: () => import("./features/notifications/notifications-page.component").then((m) => m.NotificationsPageComponent),
  },
  // Create and edit are their own routes, sharing one form component.
  {
    path: "notifications/new",
    title: "Create Notification",
    canActivate: [authGuard, surveyAdminGuard],
    loadComponent: () => import("./features/notifications/notification-form.component").then((m) => m.NotificationFormComponent),
  },
  {
    path: "notifications/:id/edit",
    title: "Edit Notification",
    canActivate: [authGuard, surveyAdminGuard],
    loadComponent: () => import("./features/notifications/notification-form.component").then((m) => m.NotificationFormComponent),
  },
  {
    path: "report-feedback",
    title: "Report a Bug or Feature",
    canActivate: [authGuard],
    loadComponent: () => import("./features/report-feedback/report-feedback.component").then((m) => m.ReportFeedbackComponent),
  },
  {
    path: "",
    children: [
      {
        path: "",
        redirectTo: "/login",
        pathMatch: "full",
      },
    ],
  },
  {
    path: "**",
    redirectTo: "/login",
  },
];
